/* eslint-disable no-use-before-define */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import CustomerSearch from 'components/site/customer-search';
import IconTextDisplay from 'components/v2/IconTextDisplay';
import CalendarIcon from 'components/v2/icons/CalendarIcon';
import LocationAutosuggest from 'components/site/location-autosuggest';
import CargomaticInput from 'components/v2/input';
import LaneRatesAppointmentWindowModal from 'components/modules/carrier/modals/LaneRatesAppointmentWindowModal';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import LocationWebAPIUtils from 'utils/LocationWebAPIUtils';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import Checkbox from '../../ui/Checkbox';
import CargomaticSelect from '../../v2/select';
import SearchBox from '../../site/SearchBox';

const ORIGIN = 'origin';
const DESTINATION = 'destination';

const dateTimeFormat = 'L';
const formatDate = (date, t) => {
  if (!date) {
    return t('NOT_SET');
  }
  return moment(date).format(dateTimeFormat);
};

const originCityClass = classnames({
  'w-full': true,
});

const destCityClass = classnames({
  'w-3/4': true,
});

const reversibleItems = [
  { name: 'Yes', value: 'Yes', localizationSlug: 'YES' },
  { name: 'No', value: 'No', localizationSlug: 'NO' },
];

const fscItems = [
  { name: getCurrencySymbol(DEFAULT_CURRENCY), value: 'dollar' },
  { name: '%', value: 'percentage' },
];
export default function LaneRatesModalContent({
  modalType,
  index,
  item,
  onLaneRateFieldChange,
  onZoneBlur,
  handleDateField,
  handleAddressUpdate,
  handleCustomerField,
  handleApplyAllButtonClick,
  onRemoveLaneFromModal,
}) {
  const [showLaneRatesAppointmentModal, setShowLaneRatesAppointmentModal] =
    useState(false);
  const [zoneList, setZoneList] = useState([]);
  const [zoneName, setZoneName] = useState('');
  const { t } = useTranslation();

  const setFilterQueryParameters = (zoneName) => {
    let filterString = {};

    if (zoneName) {
      filterString.name = zoneName;
    }

    filterString = new URLSearchParams(filterString).toString();
    return filterString;
  };

  useEffect(() => {
    const getCustomZoneList = async () => {
      try {
        let query = '';

        if (zoneName.length) {
          query = setFilterQueryParameters(zoneName);
        }

        const zoneList = await LocationWebAPIUtils.getCustomZones(`&${query}`);

        if (zoneList.zones && zoneList.zones.length) {
          setZoneList(zoneList.zones);
        }
      } catch (e) {
        console.error('failed to fetch market values: ', e);
      }
    };
    getCustomZoneList();
  }, [zoneName]);

  const renderCustomerFilter = () => {
    return (
      <CustomerSearch
        clearOnSelect={false}
        type="customer"
        id={`laneRate-${index}`}
        onSelect={(item, value) =>
          handleCustomerField('customer', item, value, modalType)
        }
        onChange={(item, value) =>
          handleCustomerField('customer', item, value, modalType)
        }
        value={item.name}
        selected={item.name}
        placeholder={t('SEARCH_FOR_CUSTOMER')}
        dropdownStyle={{ maxHeight: '160px', overflow: 'auto' }}
      />
    );
  };

  const renderCustomerInput = () => {
    return (
      <CargomaticInput
        id={`laneRate-${index}`}
        value={item.name}
        type="string"
        disabled
        hideLabel
      />
    );
  };

  const handleSearch = (query) => {
    if (!parseInt(query, 10)) {
      // Only use zone type ahead for zone names
      setZoneName(query);
    }
  };

  const handleSelect = (type, item, modalType) => {
    // zone is a flag used in this example to signify the value for the zone is a custom zone. Fulfill this condition however you'd like.
    item.zone = true;
    onLaneRateFieldChange(`${type}Zone`, item, modalType);
    setZoneName('');
  };

  const renderItem = (item) => {
    return <div>{item.name}</div>;
  };

  const renderStateInput = (isOrigin) => {
    return (
      <CargomaticInput
        id={`laneRate-state-${isOrigin}`}
        value={isOrigin ? item.originState : item.destinationState}
        type="string"
        disabled
        hideLabel
      />
    );
  };
  const renderZoneInput = (isOrigin) => {
    const originZone =
      item.originZoneName || item.originCity || item.originZip || 'CA';
    const destinationZone =
      item.destinationZoneName ||
      item.destinationCity ||
      item.destinationZip ||
      'CA';
    return (
      <CargomaticInput
        id={`laneRate-zone-${isOrigin}`}
        value={isOrigin ? originZone : destinationZone}
        type="string"
        disabled
        hideLabel
      />
    );
  };
  const renderAddressZone = (type) => {
    const originZone = item.originZoneName || item.originCity || item.originZip;
    const destinationZone =
      item.destinationZoneName || item.destinationCity || item.destinationZip;
    return (
      <div className="w-7/8 ml-4">
        <SearchBox
          id={`laneRate-${index}`}
          items={zoneList || []}
          value={
            modalType === 'update'
              ? type === 'origin'
                ? originZone
                : destinationZone
              : type === 'origin'
              ? item.originZone && item.originZone !== 0
                ? item.originZone.name
                  ? item.originZone.name
                  : item.originZone
                : ''
              : item.destinationZone && item.destinationZone !== 0
              ? item.destinationZone.name
                ? item.destinationZone.name
                : item.destinationZone
              : ''
          }
          search={(nGram) => handleSearch(nGram)}
          placeholder={
            modalType === 'update'
              ? type === 'origin'
                ? originZone
                : destinationZone
              : t('ZONE_CITY_ZIP')
          }
          onChange={(item) => {
            onLaneRateFieldChange(`${type}Zone`, item, modalType);
          }}
          onSelect={(value, item) => handleSelect(type, item, modalType)}
          onBlur={(item) => {
            lookAheadZipData(item, type);
          }}
          clearOnSelect
          disabled={modalType === 'update'}
          renderItem={renderItem}
        />
      </div>
    );
  };

  const lookAheadZipData = async (item, type) => {
    const zip = item.target.value;
    const usCanadaZipRegex = /^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d)$/;
    if (zip.length >= 5 && usCanadaZipRegex.test(zip)) {
      const zipData = await onZoneBlur(zip);
      if (zipData.length === 1) {
        onLaneRateFieldChange(`${type}State`, zipData[0].state_code, modalType);
      }
    }
  };

  const renderAddressState = (type) => {
    return (
      <div className="w-3/4 ml-4">
        <CargomaticInput
          id={`laneRate-${index}`}
          value={type === 'origin' ? item.originState : item.destinationState}
          type="string"
          placeholderText={titleize(t('STATE'))}
          onChange={(item) => {
            onLaneRateFieldChange(`${type}State`, item, modalType);
          }}
          disabled={modalType === 'update'}
          hideLabel
        />
      </div>
    );
  };

  const renderRate = () => {
    return (
      <div className="w-4/5 ml-2">
        <CargomaticInput
          id={`laneRate-${index}`}
          value={item.rate !== 0 ? item.rate : ''}
          type="number"
          min={1}
          onChange={(item) => {
            onLaneRateFieldChange('rate', item, modalType);
          }}
          hideLabel
        />
      </div>
    );
  };

  const renderFsc = () => {
    return (
      <div className="flex">
        <div className="w-4/6">
          <CargomaticInput
            id={`laneRate-${index}`}
            value={item.fsc !== 0 ? item.fsc : ''}
            type="number"
            min={1}
            hideLabel
            onChange={(item) => {
              onLaneRateFieldChange('fsc', item, modalType);
            }}
          />
        </div>
        <div className="w-1/2 ml-1">
          <CargomaticSelect
            items={fscItems}
            onSelect={(item) => {
              onLaneRateFieldChange('fscFlatRate', item.value, modalType);
            }}
            value={item.fscFlatRate}
            textKey="name"
            valueKey="value"
            hideLabel
          />
        </div>
      </div>
    );
  };

  const renderReversible = () => {
    return (
      <div className="w-2/3 ml-8">
        <CargomaticSelect
          items={reversibleItems}
          onSelect={(item) => {
            onLaneRateFieldChange('reversible', item.value, modalType);
          }}
          value={item.reversible}
          textKey="localizationSlug"
          valueKey="value"
          placeholder="Reversible"
          hideLabel
        />
      </div>
    );
  };

  const renderWeeklyCommitment = () => {
    return (
      <div className="w-3/4 ml-4">
        <CargomaticInput
          id={`laneRate-${index}`}
          value={item.weeklyCommitment !== 0 ? item.weeklyCommitment : ''}
          type="number"
          hideLabel
          onChange={(item) => {
            onLaneRateFieldChange('weeklyCommitment', item, modalType);
          }}
        />
      </div>
    );
  };

  const handleLaneRateAppointment = () => {
    setShowLaneRatesAppointmentModal(true);
  };

  const hideLaneRatesAppointmentModal = () => {
    setShowLaneRatesAppointmentModal(false);
  };

  const renderValidity = () => {
    return (
      <div className="space-y-2" id={`laneRate-${index}`}>
        <button className="block" onClick={handleLaneRateAppointment}>
          <IconTextDisplay
            color="green"
            className="mb-2"
            icon={<CalendarIcon />}
          >
            <dl className="text-left">
              <dt className="mb-1 form-label">{t('START')}</dt>
              <dd className="block text-sm text-gray-900">
                {formatDate(item.effectiveStart, t)}
              </dd>
            </dl>
          </IconTextDisplay>
          <IconTextDisplay color="red" icon={<CalendarIcon />}>
            <dl className="text-left">
              <dt className="mb-1 form-label">{t('END')}</dt>
              <dd className="block text-sm text-gray-900">
                {formatDate(item.effectiveEnd, t)}
              </dd>
            </dl>
          </IconTextDisplay>
        </button>
        <LaneRatesAppointmentWindowModal
          show={showLaneRatesAppointmentModal}
          onClose={hideLaneRatesAppointmentModal}
          validity={item}
          onStartChange={(item) => {
            handleDateField('effectiveStart', item, modalType);
          }}
          onEndChange={(item) => {
            handleDateField('effectiveEnd', item, modalType);
          }}
        />
      </div>
    );
  };

  const renderApplyAllButton = () => {
    return (
      <div className="ml-8">
        <button
          id={`laneRate-${index}`}
          className="mr-2 button button-green"
          onClick={() => handleApplyAllButtonClick(modalType)}
        >
          {t('APPLY_ALL')}
        </button>
      </div>
    );
  };

  const renderRemoveLane = () => {
    return (
      <div className="w-1/3">
        <DeleteButton onClick={() => onRemoveLaneFromModal(modalType)} />
      </div>
    );
  };

  const isRowChecked = () => {};
  const handleToggleCheck = () => {};
  return (
    <>
      <tr className="block my-4 border-none" />
      <tr className="border-none">
        <td className="p-0">
          {item.name ? renderCustomerInput() : renderCustomerFilter()}
        </td>
        <td className="p-0">{renderAddressZone(ORIGIN)}</td>
        <td className="p-0">{renderAddressState(ORIGIN)}</td>
        <td className="p-0">{renderAddressZone(DESTINATION)}</td>
        <td className="p-0">{renderAddressState(DESTINATION)}</td>
        <td className="p-0">{renderRate()}</td>
        <td className="p-0">{renderFsc()}</td>
        <td className="p-0">{renderReversible()}</td>
        <td className="p-0">{renderWeeklyCommitment()}</td>
        <td className="p-0">{renderValidity()}</td>
        <td className="p-0 w-40">{renderApplyAllButton()}</td>
        <td className="p-0">{renderRemoveLane()}</td>
      </tr>
    </>
  );
}

LaneRatesModalContent.propTypes = {
  modalType: PropTypes.string,
  index: PropTypes.number.isRequired,
  rate: PropTypes.shape({
    originZip: PropTypes.string,
    destinationZip: PropTypes.string,
    originState: PropTypes.string,
    destinationState: PropTypes.string,
    rate: PropTypes.number,
    fsc: PropTypes.number,
    weeklyCommitmentValue: PropTypes.number,
    reversible: PropTypes.bool,
  }).isRequired,
  onLaneRateFieldChange: PropTypes.func.isRequired,
  handleDateField: PropTypes.func.isRequired,
  handleAddressUpdate: PropTypes.func.isRequired,
  handleCustomerField: PropTypes.func.isRequired,
  handleApplyAllButtonClick: PropTypes.func.isRequired,
};
